// AddCompanyModal.tsx
import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader } from "components/modal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkmark, Close } from "assets/icons";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Input } from "@heroui/react";
import { API_URL } from "constants/index";
import { EditableSelectComponent } from "components/index";
import { Recycle } from "@phosphor-icons/react";
import { YallyCompany } from "shared/types/yally-company";

interface AddProductModalProps {
    companies: YallyCompany[];
    onClose?: () => void;
    isOpen: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    fetchProductTemplates: () => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
    companies,
    onClose,
    isOpen,
    onOpenChange,
    fetchProductTemplates
}) => {
    const { session } = useSession();
    const [productName, setProductName] = useState("");
    const [odooProductId, setOdooProductid] = useState("");
    const [ledger, setLedger] = useState("");
    const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    const handleClose = () => {
        onClose?.();
        onOpenChange?.(false);
    };

    const getProductDataFromOdoo = async () => {
        if (!odooProductId) {
            return toast.error("Gelieve een Odoo product ID op te geven");
        }

        try {
            const response = await fetchWithAuth(
                `${API_URL}/companies/product/odoo/${odooProductId}`,
                {
                    method: "GET",
                },
                session
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const productData = await response.json();
            console.log("Found product");
            console.log(productData);
            if (productData && productData.length == 1) {
                console.log("Setting ledger data");
                console.log(productData[0]["property_account_income_id"][1]);
                setLedger(productData[0]["property_account_income_id"][1]);
                console.log("Setting product name");
                console.log(productData[0]["name"]);
                setProductName(productData[0]["name"]);
            }

        } catch (error) {
            console.error("Error fetching product data:", error);
            toast.error("Fout bij het ophalen van product data");
        }
    }

    const handleCreateProduct = async () => {
        if (!productName) {
            return toast.error("Gelieve een productnaam op te geven");
        }

        if (!odooProductId) {
            return toast.error("Gelieve een Odoo product ID op te geven");
        }

        // Construct the company data
        const productVariant = {
            name: productName,
            companyId: companies.filter((company) => company.name === selectedCompany)[0].id,
            odooProductVariantId: odooProductId,
            ledger: (ledger && ledger.match(/\d+/)?.[0]) ?? '',  // Extract the first group of numbers or set as an empty string if null
        };

        console.log("Creating product");
        console.log(productVariant);

        try {
            // Call the create company endpoint
            const response = await fetchWithAuth(
                `${API_URL}/companies/product/create`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(productVariant),
                },
                session
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            toast.success("Product aangemaakt");
            fetchProductTemplates();
            handleClose();
        } catch (error) {
            console.error("Error creating Product:", error);
            toast.error("Fout bij het aanmaken van het Product");
        }
    };


    return (
        <Modal size="4xl" hideCloseButton className="z-0 font-normal" isOpen={isOpen}>
            <ModalContent className="flex h-[80vh] flex-col gap-6 overflow-y-scroll p-6 lg:min-w-[850px]">
                <ModalHeader className="sticky flex w-full items-center justify-between bg-linear-to-b from-white/80 to-white/1 p-0 backdrop-blur-lg">
                    <p className="text-lg">Importeer Odoo Product</p>
                    <div className="flex items-center gap-2.5">
                        <ButtonNew
                            variant="primaryMoss"
                            onClick={handleCreateProduct}
                            icon={<Checkmark />}
                            label="Bewaren"
                        />
                        <ButtonNew
                            variant="secondaryNeutralMid"
                            onClick={handleClose}
                            icon={<Close />}
                            label="Sluiten"
                        />
                    </div>
                </ModalHeader>
                <ModalBody className="flex flex-col gap-6 p-0">
                    <section className="flex flex-col gap-3.5">
                        <EditableSelectComponent label="Kies bedrijf" options={companies.map((company: YallyCompany) => ({ label: company.name, value: company.name }))} editMode={true} value={selectedCompany as string} onChange={(value: any) => setSelectedCompany(value)} />
                        <div className="flex flex-row justify-between gap-3.5">
                            <Input
                                label="Odoo product variant id"
                                value={odooProductId}
                                onChange={(e) => setOdooProductid(e.target.value)}
                                classNames={{
                                    input:
                                        "text-neutral-primary placeholder:text-neutral-secondary-mid",
                                    inputWrapper:
                                        "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                                }}
                            />
                            <ButtonNew icon={<Recycle />} label="Haal product data op uit Odoo" onClick={getProductDataFromOdoo} />
                        </div>
                        <Input
                            label="Product naam"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            classNames={{
                                input:
                                    "text-neutral-primary placeholder:text-neutral-secondary-mid",
                                inputWrapper:
                                    "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                            }}
                        />
                        <Input
                            label="Grootboekrekening (trek binnen uit Odoo)"
                            disabled={true}
                            value={ledger}
                            classNames={{
                                input:
                                    "text-neutral-primary placeholder:text-neutral-secondary-mid",
                                inputWrapper:
                                    "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                            }}
                        />

                    </section>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddProductModal;
